import { useEffect, useCallback, useMemo, useState } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { motion } from 'framer-motion';

// APIs
import {
  useGetHubContentQuery,
  useSetCountryOfResidenceMutation,
  useLogGameClickMutation,
} from '../../../api';
import { useUpdateProfileMutation } from '@gif/shared-apis';
import { useUser } from '@gif/shared-rtk';

// Types && Enums
import { Game } from '../../../types';
import { GameIds } from '../../../enums';
import { AnalyticsClickEvent } from '../../../types/Analytics';
import {
  getUserArcadeDobMonthDayYear,
  getGameUrl,
  fieldPassConfig,
  hasCompletedArcadeProfile,
} from '../../../utils';

// Hooks && Helpers
import { useArcadeUser, useBranch, useAdobeTracking } from '../../../hooks';
import { translate, config } from '../../../configs';
import { isDarkTheme } from '@mlb/play-ui';

// Components
import { ModalCloseEvent } from '@gif/shared-ui';
import {
  FieldPass,
  FieldPassChangeReasons,
  FieldPassChangeEvent,
  FieldPassCloseEvent,
  FieldPassCloseReasons,
} from '@mlb/play-ui';
import {
  Header,
  GamesList,
  SEO,
  Layout,
  AddInfoModal,
  AddInfoModalFormSubmitProps,
  AgeGateModal,
  BranchLinkModal,
  MlbPlayModal,
} from '../..';
import {
  Env,
  getProcessEnvironmentValue,
  getUserAge,
  LooseObject,
} from '@gif/shared-global';
import { useAppSelector, useAppDispatch } from '../../../store';

const StyledHeader = styled(Header)`
  margin-top: 1rem;
`;

// Copy
const closeText = translate('close');
const pageDescription = translate('mlbPlayLetsYouEnjoyFreeToPlayGames');
const pageTitle = 'Beat the Streak, MLB Pickle, By The Numbers';

const { LOGIN_SUCCESS, REGISTER_SUCCESS } = FieldPassChangeReasons;
const { BACKDROP_CLICK, CLOSE_BUTTON_CLICK, ESCAPE_KEY_PRESS, FOOTER_CLICK } =
  FieldPassCloseReasons;

const environment = (getProcessEnvironmentValue('APP_ENV') || 'prod') as Env;

const useArcadeUserProps = {
  options: {
    fixedCacheKey: 'shared-refresh-profile',
  },
};

const useUpdateProfileOptions = {
  fixedCacheKey: 'shared-update-profile',
};

const useSetCountryOfResidenceOptions = {
  fixedCacheKey: 'shared-set-country-of-residence',
};

const useLogGameClickMutationOptions = {
  fixedCacheKey: 'shared-log-game-click',
};

export const Homepage = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { trackPageView, trackClick } = useAdobeTracking();

  const {
    user,
    loadUser,
    setUser,
    isLoading: isUserLoading,
  } = useUser({
    useAppSelector,
    useAppDispatch,
  });
  const {
    loadArcadeUser,
    arcadeUser,
    isLoading: isArcadeUserLoading,
  } = useArcadeUser(useArcadeUserProps);
  /**
   * Need to pass errors into `AdditionalInformation` component to display
   * TODOs:
   *    - `updateProfileResults.error` - Display error inside of form
   *    - `updateProfileResults.isLoading` - Handle loading state
   */
  const [updateProfile, updateProfileResults] = useUpdateProfileMutation(
    useUpdateProfileOptions
  );

  const {
    branchLink,
    isBranchLinkLoading,
    branchInviteToGroupData,
    createBranchLink,
    loadInviteToGroupData,
    isBranchInviteToGroupDataLoading,
  } = useBranch();

  // TODO: Errors from this should also go into `AdditionalInformation` form
  const [setCountryOfResidence, setCountryOfResidenceResults] =
    useSetCountryOfResidenceMutation(useSetCountryOfResidenceOptions);
  const [logGameClick] = useLogGameClickMutation(
    useLogGameClickMutationOptions
  );
  const [searchParams] = useSearchParams();
  const groupIdParam = searchParams.get('groupId') || '';
  const gameIdParam = searchParams.get('gameId') || '';
  const branchMatchId = searchParams.get('_branch_match_id') || '';
  const redirectUrlParam = searchParams.get('redirectUrl') || '';
  const { gameId: branchDataGameId, groupId: branchDataGroupId } =
    branchInviteToGroupData;
  const uid = user.accessToken?.claims?.uid || '';
  const isBranchUrl = location.search.includes('branch');
  const {
    data: gamesData,
    isLoading: isGamesDataLoading,
    error: hubError,
  } = useGetHubContentQuery(
    {
      uid,
    },
    { skip: isUserLoading }
  );

  const [isNotLoggedInModalOpen, setIsNotLoggedInModalOpen] = useState(false);
  const [isFieldPassOpen, setIsFieldPassOpen] = useState<boolean | undefined>();
  const [isAddInfoMessageModalOpen, setIsAddInfoMessageModalOpen] =
    useState(false);
  const [isAddInfoModalOpen, setIsAddInfoModalOpen] = useState<
    boolean | undefined
  >();
  const [isAgeGateModalOpen, setIsAgeGateModalOpen] = useState<
    boolean | undefined
  >();
  const [isBranchLinkModalOpen, setIsBranchLinkModalOpen] = useState(false);
  const [selectedGameToRedirect, setSelectedGameToRedirect] =
    useState<Game | null>(null);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [branchLinkBody, setBranchLinkBody] = useState('');

  const header = useMemo(() => gamesData?.result?.header, [gamesData]);
  const games = useMemo(() => gamesData?.result?.games, [gamesData]);
  const loginRequirements =
    selectedGameToRedirect?.loginRequirements?.toLowerCase();
  const minimumAgeToPlayGame = selectedGameToRedirect?.minimumAge || 0;
  const selectedGameTitle = selectedGameToRedirect?.title;
  const selectedGameToRedirectUrl =
    redirectUrl ||
    getGameUrl(games, selectedGameToRedirect?.gameId, branchDataGroupId);
  const isLoginRequired = loginRequirements?.includes('required'); // required or required_bearertoken
  const isLoginOptional = loginRequirements?.includes('optional'); // optional or optional_bearertoken
  const isLoginNone = loginRequirements?.includes('none');
  const isUserLoggedIn = user.loggedIn;
  const arcadeUserBirthday = arcadeUser?.dob;
  const arcadeUserBirthdayInfo =
    getUserArcadeDobMonthDayYear(arcadeUserBirthday);
  const arcadeUserAge = getUserAge(
    new Date(
      Number(arcadeUserBirthdayInfo.birthYear),
      Number(arcadeUserBirthdayInfo.birthMonth) - 1,
      Number(arcadeUserBirthdayInfo.birthDay)
    )
  );
  const isUpdateProfileSuccess = updateProfileResults?.isSuccess;
  const isSetCountryOfResidenceSuccess =
    setCountryOfResidenceResults?.isSuccess;
  const preventCommon =
    isUserLoading ||
    selectedGameToRedirect === null ||
    (isUserLoggedIn && isArcadeUserLoading);
  const preventBranchLinkModal =
    branchMatchId ||
    !groupIdParam ||
    !gameIdParam ||
    isBranchLinkLoading ||
    !branchLink;
  const preventLoginFlow = preventCommon || !isLoginRequired || isUserLoggedIn;
  const preventAddInfoModal =
    preventCommon ||
    isLoginNone ||
    !isUserLoggedIn ||
    hasCompletedArcadeProfile(arcadeUser);
  const preventAgeGateModal =
    preventCommon ||
    !preventAddInfoModal ||
    isLoginNone ||
    isLoginOptional ||
    !isUserLoggedIn ||
    arcadeUserAge >= minimumAgeToPlayGame;
  const preventRedirect =
    preventCommon ||
    !preventLoginFlow ||
    !preventAddInfoModal ||
    !preventAgeGateModal ||
    !preventBranchLinkModal ||
    !selectedGameToRedirectUrl ||
    (!updateProfileResults?.isUninitialized && !isUpdateProfileSuccess) ||
    (!setCountryOfResidenceResults?.isUninitialized &&
      !isSetCountryOfResidenceSuccess);
  const translations = useMemo(
    () => ({
      ageGateBody: translate('mustBeMinAgeToPlay', {
        min: minimumAgeToPlayGame,
        game: selectedGameTitle,
      }),
      loginMessageBody: translate('mustLogInToAccessGame', {
        game: selectedGameTitle,
      }),
      loginMessageNote: translate('mustBeOverMinAge', {
        min: minimumAgeToPlayGame,
      }),
      additionalInfoMessageBody: translate('pleaseUpdateYourAccount', {
        game: selectedGameTitle,
      }),
    }),
    [minimumAgeToPlayGame, selectedGameTitle]
  );

  const onFieldPassLoginSuccessHandler = useCallback(
    (event: FieldPassChangeEvent) => {
      // Analytics Page Tracking
      trackClick({
        type: 'Gaming Hub',
        subType: 'Log In',
        event: 'log in click',
        eventText: 'log in',
        eventIndex: '1',
        targetURL: '/play',
      });
      // End Analytics Page Tracking
      setUser({
        ...event.data,
        loggedIn: true,
      });
      setIsFieldPassOpen(false);
    },
    [setUser, trackClick]
  );
  const onFieldPassRegisterSuccessHandler = useCallback(() => {
    // we are doing this because user data is not available to us in the event on successful registration
    trackClick({
      type: 'Gaming Hub',
      subType: 'Register',
      event: 'register click',
      eventText: 'register',
      eventIndex: '1',
      targetURL: '/play',
    });
    // End Analytics Page Tracking
    loadUser();
    setIsFieldPassOpen(false);
  }, [loadUser, trackClick]);
  const FieldPassChangeReasonMapHandler: LooseObject = useMemo(
    () => ({
      [LOGIN_SUCCESS]: onFieldPassLoginSuccessHandler,
      [REGISTER_SUCCESS]: onFieldPassRegisterSuccessHandler,
    }),
    [onFieldPassLoginSuccessHandler, onFieldPassRegisterSuccessHandler]
  );
  const onFieldPassChangeHandler = useCallback(
    (event: FieldPassChangeEvent) => {
      FieldPassChangeReasonMapHandler[event.reason]?.(event);
    },
    [FieldPassChangeReasonMapHandler]
  );
  const onFieldPassCloseHandler = useCallback(
    (event: FieldPassCloseEvent) => {
      trackClick({
        type: 'Game Hub',
        subType: 'Login Modal',
        event: 'close click',
        eventText: 'close log in modal',
        eventIndex: '0',
      });
      setIsFieldPassOpen(false);
      const reason = event?.reason;
      if (
        reason === BACKDROP_CLICK ||
        reason === CLOSE_BUTTON_CLICK ||
        reason === ESCAPE_KEY_PRESS ||
        reason === FOOTER_CLICK
      ) {
        setSelectedGameToRedirect(null);
      }
    },
    [trackClick]
  );
  // Toggling isAddInfoModalOpen state to false triggers this
  const onAddInfoModalCloseHandler = useCallback(
    (event: ModalCloseEvent) => {
      setIsAddInfoModalOpen(false);
      const eventType = event?.type;
      if (eventType === 'click' || eventType === 'cancel') {
        trackClick({
          type: 'Game Hub',
          subType: 'Additional Information Modal',
          event: 'close click',
          eventText: 'close additional information modal',
          eventIndex: '0',
        });
        setSelectedGameToRedirect(null);
      }
    },
    [trackClick]
  );

  const onAddInfoModalFormSubmitHandler = useCallback(
    (data: AddInfoModalFormSubmitProps) => {
      const {
        nickName,
        birthDay: day,
        birthMonth: month,
        birthYear: year,
        country,
      } = data;
      if (!nickName || !day || !month || !year || !country) {
        return;
      }
      const birthDay = Number(day);
      const birthMonth = Number(month);
      const birthYear = Number(year);

      setUser({
        ...user,
        idToken: {
          ...user.idToken,
          claims: {
            ...user.idToken?.claims,
            birthDay,
            birthMonth,
            birthYear,
          },
        },
      });
      updateProfile({
        nickName,
        birthDay,
        birthMonth,
        birthYear,
        baseUrl: config.oktaApiUrl,
      });
      setCountryOfResidence({
        uid,
        ...country,
      });
      trackClick({
        type: 'Game Hub',
        subType: 'Additional Information Modal',
        event: 'update information click',
        eventText: 'update information',
        eventIndex: '1',
        targetURL: selectedGameToRedirect?.uri,
      });
      setIsAddInfoModalOpen(false);
    },
    [
      setUser,
      user,
      updateProfile,
      setCountryOfResidence,
      uid,
      trackClick,
      selectedGameToRedirect?.uri,
    ]
  );
  const setGame = useCallback(
    (
      gameId: string | undefined | null,
      analytics?: AnalyticsClickEvent | null
    ) => {
      const game = games?.find((game) => game.gameId === gameId);
      if (!game || !gameId) {
        return null;
      }
      if (!preventAddInfoModal) {
        setIsAddInfoMessageModalOpen(true);
        return;
      }
      if (analytics) {
        trackClick({ ...analytics, targetURL: game.uri });
      }
      setSelectedGameToRedirect(game);
      return game;
    },
    [games, preventAddInfoModal, trackClick]
  );
  const handleHeaderClick = useCallback(() => {
    if (!header) {
      return;
    }
    const { type, gameId, webClientLink } = header;
    if (type === 'GAME') {
      const headerClickAnalytics = {
        type: 'Gaming Hub',
        event: 'header: content media click',
        gameID: gameId,
        eventIndex: '0',
      };
      setRedirectUrl(null);
      setGame(gameId, headerClickAnalytics);
    } else if (webClientLink) {
      window.location.href = webClientLink;
    }
  }, [header, setGame]);
  const onGameCardClickHandler = useCallback(
    (event: React.MouseEvent, i: number) => {
      event.preventDefault();
      const gameId = event.currentTarget.getAttribute('data-game-id');
      const gameCardClickAnalytics = {
        type: 'Gaming Hub',
        event: 'contentCarousel: content card media click',
        gameID: gameId,
        eventIndex: `${i}`,
      };
      setRedirectUrl(null);
      setGame(gameId, gameCardClickAnalytics);
    },
    [setGame]
  );

  const handleAgeGateClose = useCallback(() => {
    trackClick({
      type: 'Game Hub',
      subType: 'Age Gating Modal',
      event: 'close click',
      eventText: 'close age gate modal',
      eventIndex: '0',
    });
    setSelectedGameToRedirect(null);
    setIsAgeGateModalOpen(false);
  }, [trackClick]);

  const handleBranchLinkClose = useCallback(() => {
    trackClick({
      type: 'Game Hub',
      subType: 'Branch Link Modal',
      event: 'close click',
      eventText: 'close branch link modal',
      eventIndex: '0',
    });
    setIsBranchLinkModalOpen(false);
  }, [trackClick]);

  const handleNotLoggedInModalClose = useCallback(
    (event: ModalCloseEvent) => {
      /**
       * This modal can be dismissed via button or close icon
       * When it's dismissed via button, field pass opens and it should remember the selected game
       * When it's dismissed via close icon, the selected game should be cleared
       *
       * `event` only exists when you click on the close icon
       */
      if (event) {
        trackClick({
          type: 'Game Hub',
          subType: 'Not Logged In Modal',
          event: 'close click',
          eventText: 'close not logged in modal',
          eventIndex: '0',
        });
        setSelectedGameToRedirect(null);
      }
      setIsNotLoggedInModalOpen(false);
    },
    [trackClick]
  );
  // Proceeds to field pass login
  const handleNotLoggedInModalButtonClick = useCallback(() => {
    setIsNotLoggedInModalOpen(false);
    setIsFieldPassOpen(true);
  }, []);
  const handleAddInfoMessageClose = useCallback(
    (event: ModalCloseEvent) => {
      if (event) {
        trackClick({
          type: 'Game Hub',
          subType: 'Additional Information Message Modal',
          event: 'close click',
          eventText: 'close additional information message modal',
          eventIndex: '0',
        });
        setSelectedGameToRedirect(null);
      }
      setIsAddInfoMessageModalOpen(false);
    },
    [trackClick]
  );

  const handleAddInfoMessageButtonClick = useCallback(() => {
    trackClick({
      type: 'Game Hub',
      subType: 'Additional Information Message Modal',
      event: 'close click',
      eventText: 'close additional information message modal',
      eventIndex: '1',
    });
    setIsAddInfoMessageModalOpen(false);
    setIsAddInfoModalOpen(true);
  }, [trackClick]);

  /*
    Handle Branch Urls and Data Area
  */

  useEffect(() => {
    // create branch link using groupId and gameId query params
    const game = games?.find((game) => game.gameId === gameIdParam);
    if (!gameIdParam || !groupIdParam || branchMatchId || !game) {
      return;
    }
    const logo = game.logo;
    createBranchLink({
      feature: 'sharing',
      gameId: gameIdParam,
      groupId: groupIdParam,
      gameTitle: game.title,
      gameLogo: isDarkTheme(theme) ? logo.darkUrl : logo.lightUrl,
    });
    setBranchLinkBody(
      translate('sharingThisLinkAddsMembers', {
        game: game.title,
      })
    );
  }, [
    gameIdParam,
    groupIdParam,
    branchMatchId,
    games,
    theme,
    createBranchLink,
  ]);

  useEffect(() => {
    // load branch data if branch url
    if (!isBranchUrl || !branchMatchId) {
      return;
    }
    // provides invite to group data to store
    loadInviteToGroupData();
  }, [loadInviteToGroupData, isBranchUrl, branchMatchId]);

  useEffect(() => {
    // set if only gameId using branch is provided
    if (
      isGamesDataLoading ||
      !games?.length ||
      isBranchInviteToGroupDataLoading ||
      !branchDataGameId ||
      !branchDataGroupId ||
      !isBranchUrl ||
      !branchMatchId
    ) {
      return;
    }
    setGame(branchDataGameId);
    navigate(location.pathname, { replace: true });
  }, [
    branchDataGameId,
    branchDataGroupId,
    branchMatchId,
    games?.length,
    isBranchInviteToGroupDataLoading,
    isBranchUrl,
    isGamesDataLoading,
    location.pathname,
    navigate,
    setGame,
  ]);

  useEffect(() => {
    // trigger login flow when not logged in and handling branch link and redirect to specified url
    if (
      !branchDataGameId ||
      !branchDataGroupId ||
      isBranchInviteToGroupDataLoading ||
      !games?.length
    ) {
      return;
    }
    const branchJoinGroupUrl = getGameUrl(
      games,
      branchDataGameId,
      branchDataGroupId
    );
    setRedirectUrl(branchJoinGroupUrl);
  }, [
    branchDataGameId,
    branchDataGroupId,
    games,
    isBranchInviteToGroupDataLoading,
  ]);

  // clean up url after Branch link modals or urls
  // for example - branch link modal creation
  useEffect(() => {
    if (!branchLink || !gameIdParam || !groupIdParam || isGamesDataLoading) {
      return;
    }
    navigate(location.pathname, { replace: true });
  }, [
    branchLink,
    gameIdParam,
    groupIdParam,
    isGamesDataLoading,
    location.pathname,
    navigate,
  ]);
  /*
    End handle Branch Urls and Data Area
  */

  // set selected game using gameId query params
  useEffect(() => {
    if (!gameIdParam || isGamesDataLoading || !games?.length || groupIdParam) {
      return;
    }
    setGame(gameIdParam);
    navigate(location.pathname, { replace: true });
  }, [
    setGame,
    games,
    isGamesDataLoading,
    gameIdParam,
    navigate,
    location,
    groupIdParam,
  ]);

  // set redirect url using redirectUrl query param
  useEffect(() => {
    if (!redirectUrlParam) {
      return;
    }
    setRedirectUrl(redirectUrlParam);
    if (!gameIdParam && !groupIdParam) {
      navigate(location.pathname, { replace: true });
    }
  }, [navigate, location, redirectUrlParam, gameIdParam, groupIdParam]);

  // soft refresh arcade profile
  useEffect(() => {
    if (isUserLoading || !isUserLoggedIn) {
      return;
    }
    loadArcadeUser();
  }, [isUserLoggedIn, loadArcadeUser, isUserLoading]);
  // hard refresh arcade profile
  useEffect(() => {
    if (!isUpdateProfileSuccess || !isSetCountryOfResidenceSuccess) {
      return;
    }
    loadArcadeUser(true);
  }, [isUpdateProfileSuccess, isSetCountryOfResidenceSuccess, loadArcadeUser]);
  // show not logged in modal
  useEffect(() => {
    if (preventLoginFlow) {
      return;
    }
    setIsNotLoggedInModalOpen(true);
  }, [preventLoginFlow]);
  // show add info message modal
  useEffect(() => {
    if (preventAddInfoModal) {
      return;
    }
    setIsAddInfoMessageModalOpen(true);
  }, [preventAddInfoModal]);
  // show age gating modal
  useEffect(() => {
    if (preventAgeGateModal) {
      return;
    }
    setIsAgeGateModalOpen(true);
  }, [preventAgeGateModal]);
  // show branch link modal
  useEffect(() => {
    if (preventBranchLinkModal) {
      return;
    }
    setIsBranchLinkModalOpen(true);
  }, [preventBranchLinkModal]);
  // redirect
  useEffect(() => {
    if (preventRedirect) {
      return;
    }
    setSelectedGameToRedirect(null);
    logGameClick({ gameId: selectedGameToRedirect?.gameId, uid })
      .unwrap() // must run this to access payload
      .then(() => {
        window.location.href =
          selectedGameToRedirect?.gameId === GameIds.PICKLE
            ? `/play${selectedGameToRedirectUrl}`
            : selectedGameToRedirectUrl;
      })
      .catch((error: Error) => {
        console.error(error);
        window.location.href =
          selectedGameToRedirect?.gameId === GameIds.PICKLE
            ? `/play${selectedGameToRedirectUrl}`
            : selectedGameToRedirectUrl;
      });
  }, [
    preventRedirect,
    selectedGameToRedirect,
    uid,
    logGameClick,
    selectedGameToRedirectUrl,
  ]);
  // Listen to open of field pass
  // @TODO - FieldPass component needs to implement onOpen prop
  useEffect(() => {
    if (isFieldPassOpen) {
      // Analytics Page Tracking
      trackPageView({
        type: 'Gaming Hub',
        subType: 'Log In',
      });
      // End Analytics Page Tracking
    }
  }, [isFieldPassOpen, trackPageView]);

  // Analytics Page Tracking
  useEffect(() => {
    if (!isGamesDataLoading && !isUserLoading) {
      trackPageView({
        type: 'Gaming Hub',
        subType: '',
      });
    }
  }, [isGamesDataLoading, isUserLoading, trackPageView]);
  // End Analytics Page Tracking

  if (hubError) {
    throw hubError;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Layout hideFooterLegalText={false}>
        <SEO title={pageTitle} description={pageDescription} />
        <StyledHeader
          header={header}
          games={games}
          onClick={handleHeaderClick}
          branchDataGroupId={branchDataGroupId}
          isLoading={isGamesDataLoading}
        />
        <GamesList
          games={games}
          title={translate('allGames')}
          onGameCardClick={onGameCardClickHandler}
          branchDataGroupId={branchDataGroupId}
          isLoading={isGamesDataLoading}
        />
        <MlbPlayModal
          isOpen={isNotLoggedInModalOpen}
          testId="notLoggedInModal"
          title={translate('pleaseLogInOrCreateAccount')}
          closeButtonAria={translate('dismissLoginMessageModal')}
          closeButtonAlt={closeText}
          buttonText={translate('logInToMLB')}
          onClose={handleNotLoggedInModalClose}
          onButtonClick={handleNotLoggedInModalButtonClick}
          bodyText={translations.loginMessageBody}
          {...(minimumAgeToPlayGame && {
            noteText: translations.loginMessageNote,
          })}
        />
        <FieldPass
          isOpen={isFieldPassOpen}
          isLoggedIn={isUserLoggedIn}
          onClose={onFieldPassCloseHandler}
          onChange={onFieldPassChangeHandler}
          env={environment}
          copyConfig={fieldPassConfig['copyConfig']}
          loginConfig={fieldPassConfig['loginConfig']}
          registerConfig={fieldPassConfig['registerConfig']}
        />
        <MlbPlayModal
          isOpen={isAddInfoMessageModalOpen}
          testId="addInfoMessageModal"
          title={translate('updateAccountInformation')}
          buttonText={translate('updateMyAccount')}
          closeButtonAria={translate('dismissUpdateAccountModal')}
          closeButtonAlt={closeText}
          onClose={handleAddInfoMessageClose}
          onButtonClick={handleAddInfoMessageButtonClick}
          bodyText={translations.additionalInfoMessageBody}
        />
        <AddInfoModal
          isOpen={isAddInfoModalOpen}
          onClose={onAddInfoModalCloseHandler}
          onFormSubmit={onAddInfoModalFormSubmitHandler}
          testId={'addInfoModal'}
        />
        <AgeGateModal
          testId="ageGateModal"
          isOpen={isAgeGateModalOpen}
          showCloseIcon={true}
          closeButtonAria={translate('dismissAgeRestrictionModal')}
          closeButtonAlt={closeText}
          onClose={handleAgeGateClose}
          onButtonClick={handleAgeGateClose}
          body={translations.ageGateBody}
        />
        <BranchLinkModal
          title={translate('readyToShareTheFun')}
          bodyText={branchLinkBody}
          link={branchLink}
          isOpen={isBranchLinkModalOpen}
          testId={'branchLink'}
          onClose={handleBranchLinkClose}
        />
      </Layout>
    </motion.div>
  );
};

export default Homepage;
